import React, { useState, useEffect } from 'react';

interface Project {
  ProjectId: string;
  Title: string;
  Status: string;
  ProjectManagerEmail?: string;
}

interface TeamLead {
  UserId: string;
  Email: string;
  Name: string;
}

export default function ProjectManagerAssignment() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [teamLeads, setTeamLeads] = useState<TeamLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projRes, leadsRes] = await Promise.all([
        fetch('/projects'),
        fetch('/users?role=TeamLead'),
      ]);

      const projData = await projRes.json();
      const leadsData = await leadsRes.json();

      const readyProjects = (projData.data || []).filter(
        (p: Project) => p.Status === 'BelowTheLine' || p.Status === 'ReadyForDelivery' || p.Status === 'InDelivery'
      );

      setProjects(readyProjects);
      setTeamLeads(leadsData.data || []);

      // Initialize assignments
      const initAssignments: Record<string, string> = {};
      readyProjects.forEach((p: Project) => {
        if (p.ProjectManagerEmail) {
          initAssignments[p.ProjectId] = p.ProjectManagerEmail;
        }
      });
      setAssignments(initAssignments);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentChange = (projectId: string, email: string) => {
    setAssignments({
      ...assignments,
      [projectId]: email,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      for (const [projectId, pmEmail] of Object.entries(assignments)) {
        const res = await fetch(`/projects/${projectId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectManagerEmail: pmEmail || null }),
        });

        const data = await res.json();
        if (!data.success) {
          setError(`Failed to save assignment for ${projectId}: ${data.error}`);
          setSaving(false);
          return;
        }
      }

      setSuccess('All assignments saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save assignments');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading projects and team leads...</div>;
  }

  const unassignedCount = projects.filter(p => !assignments[p.ProjectId]).length;

  return (
    <div style={{ marginBottom: '2rem' }}>
      <h2>Project Manager Assignments</h2>
      <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
        Assign Team Leads as Project Managers to projects in Ready for Delivery, Below the Line, or In Delivery status.
        PMs can then submit health reports and track their project timesheets.
      </p>

      {error && <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>{error}</div>}
      {success && <div style={{ backgroundColor: '#d4edda', color: '#155724', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>{success}</div>}

      {projects.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>
          <p>No projects in Ready for Delivery, Below the Line, or In Delivery status</p>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#666' }}>
            {unassignedCount} of {projects.length} projects unassigned
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Project</th>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Assign PM</th>
              </tr>
            </thead>
            <tbody>
              {projects.map(project => (
                <tr key={project.ProjectId} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontWeight: 600 }}>{project.Title}</div>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>{project.ProjectId}</div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                    }}>
                      {project.Status}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <select
                      value={assignments[project.ProjectId] || ''}
                      onChange={(e) => handleAssignmentChange(project.ProjectId, e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        minWidth: '250px',
                      }}
                    >
                      <option value="">-- Select Team Lead --</option>
                      {teamLeads.map(lead => (
                        <option key={lead.Email} value={lead.Email}>
                          {lead.Name} ({lead.Email})
                        </option>
                      ))}
                    </select>
                    {assignments[project.ProjectId] && (
                      <button
                        onClick={() => handleAssignmentChange(project.ProjectId, '')}
                        style={{
                          marginLeft: '0.5rem',
                          padding: '0.4rem 0.8rem',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 600,
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving...' : 'Save All Assignments'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
